import React, { useContext, useState, useEffect, useCallback } from "react";
import { LButton, LFormInput } from "@loffa/gpp-modules";
import { AuthContext } from "../../../../../../../context/AuthContext";
import styles from './CPConfigManagement.module.css';
import LoadingOverlay from "src/components/Shared/LoadingOverlay/LoadingOverlay";

const GATEWAY_BASE = process.env.REACT_APP_API_GATEWAY_BASE;

function CPConfigurationManagement(props) {  
  const context = useContext(AuthContext);
  const [state, setState] = useState({
    clientGuid: props.clientGuid,
    productGuid: props.productGuid,  
    productTypeId: {},
    clientProduct: {},
    storageTypes: [],
    isModified: false,
    isLoading: false
  });
  const [dtcState, setDTCState] = useState({
    DTCs: []
  });
  const [m365Enabled, setM365Enabled] = useState(false);
  const [resetState, setResetState] = useState({
    confirming: false,
    submitting: false,
    result: null,
    error: null,
  });

  useEffect(() => {
    fetch(GATEWAY_BASE + "/multitenancy/api/Products/" + props.productGuid + "/Microsoft365", {
      method: "GET",
      mode: "cors",
      headers: {
        Authorization: `Bearer ${context.authState().accessToken.accessToken}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data) => setM365Enabled(data.enabled ?? false))
      .catch((err) => console.log("Failed to load M365 config:", err));
  }, [context, props.productGuid]);

  const getBaseProductUrl = useCallback(async () => {
    let productUrl =
      GATEWAY_BASE + "/multitenancy/api/products/" + props.productGuid;
  
    try {
      const res = await fetch(productUrl, {
        method: "GET",
        mode: "cors",
        headers: {
          Authorization: `Bearer ${context.authState().accessToken.accessToken}`,
          "Content-Type": "application/json",
        },
      });
      const response = await res.json();
  
      // Note: hijacking this call to retrieve the product type id
      const productTypeId = response.productTypeId;
  
      // Now, set the state and wait for the update
      setState(prevState => ({
        ...prevState,        
        productTypeId
      }));
  
      return { baseUrl: response.url, productTypeId };
    } catch (error) {
      console.log(error);
    }
  }, [context, props.productGuid]);
  
  const syncAssociatedDTC = async () => {
    try {
      // Get and set associated DTC
      const { baseUrl, productTypeId } = await getBaseProductUrl();
     
      const lowerCaseProductTypeId = productTypeId.toLowerCase();
      const id1 = "9DF3F6BA-4F6F-44DA-AF2E-6C05356B0545".toLowerCase(); //FVD-R
      const id2 = "B2649D41-F6F0-4075-B912-06C7A6A62B6F".toLowerCase(); //FVD-S
  
      // Compare the productTypeId and set the appropriate URL
      let productUrl;
      if (lowerCaseProductTypeId === id1 || lowerCaseProductTypeId === id2) {
        productUrl = `${baseUrl}/api/Multitenancy/GetAssociatedDTCs/${state.clientGuid}/${productTypeId}`;
      } else {
        productUrl = `${baseUrl}/api/multitenancy/GetAllAssociatedDTCs`;
      }
  
      const response = await fetch(productUrl, {
        mode: "cors",
        method: "GET",
        headers: {
          Authorization: `Bearer ${context.authState().accessToken.accessToken}`,
          AccessToken: `${context.authState().accessToken.accessToken}`,
          ClientGuid: props.clientGuid,
        }
      });
  
      if (!response.ok) {
        console.warn("GetAssociatedDTCs failed:", response.status, await response.text());
        setDTCState(s => ({
          ...s,
          DTCs: []
        }));
        return;
      }
  
      const dtcList = await response.json();
  
      const dtcs = Array.isArray(dtcList.DTCs) ? dtcList.DTCs : [];

      if (dtcList.DTCs == null) { 
        console.warn("GetAssociatedDTCs returned no DTCs:", dtcList);
      }
      
      try{
        const uri = `${GATEWAY_BASE}/multitenancy/api/Integration/${props.clientGuid}/${props.productGuid}/UpdateDTCs`;
        const updateDtcsRes = await fetch(uri, {
          mode: "cors",
          headers: {
            Authorization: `Bearer ${context.authState().accessToken.accessToken}`,
            'Content-Type': 'application/json'
          },
          method: "PUT",
          body: JSON.stringify(dtcs)
        });
        if (!updateDtcsRes.ok) {
          throw new Error(`UpdateDTCs failed: ${updateDtcsRes.status}`);
        }
      } catch(er) {
        console.error("Error updating DTCs:", er);
      }
      
      setDTCState(s => ({
        ...s,
        DTCs: dtcs
      }));

    } catch (error) {
      console.error("Error fetching DTCs:", error);
      setDTCState(s => ({
        ...s,
        DTCs: ["Error syncing Associated DTC#s. Is the application and database configuration available for use? See console log."],
      }));
    }
  };
  

  const bindData = useCallback(async () => {
    setState(s => ({ ...s, isLoading: true }))

    if (props.clientGuid !== null) {
      await syncAssociatedDTC();
    }

    let url =
      GATEWAY_BASE +
      "/multitenancy/api/Clients/" +
      state.clientGuid +
      "/Products/" +
      state.productGuid +
      "/Configuration";

    fetch(url, {
      method: "GET",
      mode: "cors",
      headers: {
        Authorization: `Bearer ${context.authState().accessToken.accessToken}`,
      },
    })
      .then((res) => {
        if (res.status === 204) {
          return [];
        } else {
          return res.json();
        }
      })
      .then(function (response) {
        setState(s => ({
          ...s,
          clientProduct: response.clientProduct,
          initialClientProduct: response.clientProduct,
          storageTypes: response.storageTypes,
          isLoading: false
        }));
      })
      .catch((error) => {
        console.log(error);
        setState(s => ({
          ...s,
          isLoading: false
        }));
      });
  }, [context, state.clientGuid, state.productGuid]);

  useEffect(() => {
    bindData();
  }, [bindData]);

  const resetForm = () => {
    setState({ ...state, clientProduct: state.initialClientProduct });
  };

  const getComboBoxValue = (types, id) => {
    return types.find((st) => st.id === id);
  };

  const handleStateChange = (e) => {
    setState({
      ...state,
      clientProduct: {
        ...state.clientProduct,
        [e.target.name]: e.target.value,
        lastUpdatedBy: context.userInfo().loffaUserGuid,
      },
      isModified: true,
    });
  };

  const handleComboBoxChange = (e) => {
    setState({
      ...state,
      clientProduct: {
        ...state.clientProduct,
        [e.target.name]: e.target.value.id,
      },
      isModified: true,
    });
  };

  const handleSubmit = (e) => {
    e.currentTarget.checkValidity();
    e.preventDefault();
    let authContext = context;
    let method = "PUT";
    let url =
      GATEWAY_BASE +
      "/multitenancy/api/Clients/" +
      state.clientGuid +
      "/Products/" +
      state.productGuid +
      "/Configuration";

    fetch(url, {
      method: method,
      mode: "cors",
      headers: {
        Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(state.clientProduct),
    }).then(
      (res) => {
        if (res.status === 200) {
          setState({
            ...state,
            isModified: false,
          });
        }

        if (props.saveSuccessForm) {
          props.saveSuccessForm();
        }
      },
      (error) => {
        console.log(error);
      }
    );
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).catch((err) => console.log("Copy failed:", err));
  };

  const cancelReset = () => setResetState((s) => ({ ...s, confirming: false, error: null }));

  const handleReset = () => {
    setResetState((s) => ({ ...s, submitting: true, error: null }));

    let url =
      GATEWAY_BASE +
      "/multitenancy/api/Provisioning/Client/" +
      state.clientGuid +
      "/Products/" +
      state.productGuid +
      "/Reset";

    fetch(url, {
      method: "POST",
      mode: "cors",
      headers: {
        Authorization: `Bearer ${context.authState().accessToken.accessToken}`,
      },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((result) => {
        setResetState({ confirming: false, submitting: false, result, error: null });
      })
      .catch((error) => {
        console.log(error);
        setResetState((s) => ({
          ...s,
          submitting: false,
          error: "Reset failed. See console log.",
        }));
      });
  };

  const loadingPanel = <LoadingOverlay loadingText="Loading"/>

  return (
    <div >
      {state.isLoading && loadingPanel}
      <form onSubmit={handleSubmit}>
        {props.clientGuid !== null && (
          <div>
            <div className={styles.guidRight}>
              Client Product ID: {state.clientProduct.clientProductGuid}
            </div>
            <div className={styles.configHeader}>E2E</div>
            <LFormInput
              labelName="E2E Enabled"
              type="switch"
              onChange={(event) => handleStateChange(event)}
              switch={{
                value: state.clientProduct.e2EIsEnabled,
                onChange: (event) => handleStateChange(event),
                name: "e2EIsEnabled",
                onLabel: "Enabled",
                offLabel: "Disabled",
                checked: state.clientProduct.e2EIsEnabled,
                width: 90
              }}
            />
          </div>
        )}
        <div>
          <div className={styles.configHeader}>Database</div>
          <LFormInput
            labelName="Database Server"
            type="input"
            onChange={(event) => handleStateChange(event)}
            input={{
              value: state.clientProduct.databaseServer,
              onChange: (event) => handleStateChange(event),
              name: "databaseServer",
            }}
          />
          <LFormInput
            labelName="Database Catalog"
            type="input"
            onChange={(event) => handleStateChange(event)}
            input={{
              value: state.clientProduct.databaseCatalog,
              onChange: (event) => handleStateChange(event),
              name: "databaseCatalog",
            }}
          />
          <LFormInput
            labelName="Database User"
            type="input"
            onChange={(event) => handleStateChange(event)}
            input={{
              value: state.clientProduct.databaseUser,
              onChange: (event) => handleStateChange(event),
              name: "databaseUser",
            }}
          />
          <LFormInput
            labelName="Database Password"
            type="input"
            onChange={(event) => handleStateChange(event)}
            input={{
              value: state.clientProduct.databasePassword,
              onChange: (event) => handleStateChange(event),
              name: "databasePassword",
            }}
          />

          <div className={styles.configHeader}>Auth Database</div>
          <LFormInput
            labelName="Auth Database Server"
            type="input"
            onChange={(event) => handleStateChange(event)}
            input={{
              value: state.clientProduct.authDatabaseServer,
              onChange: (event) => handleStateChange(event),
              name: "authDatabaseServer",
            }}
          />
          <LFormInput
            labelName="Auth Database Catalog"
            type="input"
            onChange={(event) => handleStateChange(event)}
            input={{
              value: state.clientProduct.authDatabaseCatalog,
              onChange: (event) => handleStateChange(event),
              name: "authDatabaseCatalog",
            }}
          />
          <LFormInput
            labelName="Auth Database User"
            type="input"
            onChange={(event) => handleStateChange(event)}
            input={{
              value: state.clientProduct.authDatabaseUser,
              onChange: (event) => handleStateChange(event),
              name: "authDatabaseUser",
            }}
          />
          <LFormInput
            labelName="Auth Database Password"
            type="input"
            onChange={(event) => handleStateChange(event)}
            input={{
              value: state.clientProduct.authDatabasePassword,
              onChange: (event) => handleStateChange(event),
              name: "authDatabasePassword",
            }}
          />

          <div className={styles.configHeader}>Storage</div>
          <p style={{ fontSize: "12px", color: "#777", margin: "0 15px 18px" }}>
            Storage Server User Name and Password are the shared per-environment storage
            account and key — the same for every client in this environment, copied from an
            existing client. Only the Storage Path is unique per client.
          </p>
          <LFormInput
            labelName="Storage Type"
            type="combobox"
            comboBox={{
              name: "storageTypeId",
              data: state.storageTypes,
              textField: "name",
              dataItemKey: "id",
              value: getComboBoxValue(
                state.storageTypes,
                state.clientProduct.storageTypeId
              ),
              onChange: (event) => handleComboBoxChange(event),
            }}
          />
          <LFormInput
            labelName="Storage Server User Name"
            type="input"
            onChange={(event) => handleStateChange(event)}
            input={{
              value: state.clientProduct.storageServerUserName,
              onChange: (event) => handleStateChange(event),
              name: "storageServerUserName",
            }}
          />
          <LFormInput
            labelName="Storage Server User Password"
            type="input"
            onChange={(event) => handleStateChange(event)}
            input={{
              value: state.clientProduct.storageServerUserPassword,
              onChange: (event) => handleStateChange(event),
              name: "storageServerUserPassword",
            }}
          />
          <LFormInput
            labelName="Storage Path"
            type="input"
            onChange={(event) => handleStateChange(event)}
            input={{
              value: state.clientProduct.storagePath,
              onChange: (event) => handleStateChange(event),
              name: "storagePath",
            }}
          />

          <div className={styles.configHeader}>Incoming Handler</div>
          {m365Enabled && (
            <p style={{ color: "#555", fontStyle: "italic", marginBottom: "8px" }}>
              Microsoft 365 is enabled for this product — IMAP settings (Email Server, Password, and Port) are not used and have been disabled.
            </p>
          )}
          <LFormInput
            labelName="Email Server"
            type="input"
            input={{
              value: state.clientProduct.incomingHandlerEmailServer,
              onChange: (event) => handleStateChange(event),
              name: "incomingHandlerEmailServer",
              disabled: m365Enabled,
            }}
          />
          <LFormInput
            labelName="Email Username"
            type="input"
            input={{
              value: state.clientProduct.incomingHandlerEmailUser,
              onChange: (event) => handleStateChange(event),
              name: "incomingHandlerEmailUser",
            }}
          />
          <LFormInput
            labelName="Email Password"
            type="input"
            input={{
              value: state.clientProduct.incomingHandlerEmailPassword,
              onChange: (event) => handleStateChange(event),
              name: "incomingHandlerEmailPassword",
              disabled: m365Enabled,
            }}
          />
          <LFormInput
            labelName="Email Port"
            type="input"
            input={{
              value: state.clientProduct.incomingHandlerConnectionPort,
              onChange: (event) => handleStateChange(event),
              name: "incomingHandlerConnectionPort",
              disabled: m365Enabled,
            }}
          />
          {props.clientGuid && (
              <div>
                <div className={styles.configHeader}>Associated DTCs</div>
                <ol>
                {dtcState.DTCs?.length > 0
                  ? dtcState.DTCs.map(item => <li key={item}>{item}</li>)
                  : <li>No associated DTCs found</li>
                }
                </ol>
              </div>
            )}


        </div>
        {state.isModified && (
          <div>
            <LButton onClick={resetForm} type="button">
              Discard Changes
            </LButton>
            <span> </span>
            <LButton themeColor="primary" className="green" type="submit">
              Save
            </LButton>
          </div>
        )}
      </form>

      {props.clientGuid !== null && (
        <div style={{ marginTop: "24px" }}>
          <div className={styles.configHeader}>Provisioning</div>
          {resetState.result ? (
            <div style={{ border: "1px solid #cfe8cf", background: "#f2fbf2", padding: "12px", borderRadius: "4px" }}>
              <div>Product removed — it's now available to re-provision from this client's product list (click Back).</div>
              <div style={{ marginTop: "6px" }}>
                <strong>The database was NOT dropped.</strong> Drop it manually in SQL before re-provisioning:
              </div>
              <div style={{ marginTop: "6px", display: "flex", alignItems: "center", gap: "8px" }}>
                <code style={{ fontWeight: "bold" }}>
                  {resetState.result.clearedDatabaseCatalog
                    ? (resetState.result.clearedDatabaseServer
                        ? `${resetState.result.clearedDatabaseServer} / ${resetState.result.clearedDatabaseCatalog}`
                        : resetState.result.clearedDatabaseCatalog)
                    : "(no database was configured)"}
                </code>
                {resetState.result.clearedDatabaseCatalog && (
                  <LButton type="button" fillMode="flat" onClick={() => copyToClipboard(resetState.result.clearedDatabaseCatalog)}>
                    Copy name
                  </LButton>
                )}
              </div>
            </div>
          ) : resetState.confirming ? (
            <div style={{ border: "1px solid #e6d8a8", background: "#fdf8e8", padding: "12px", borderRadius: "4px" }}>
              <div><strong>Reset provisioning for this client-product?</strong></div>
              <div style={{ marginTop: "6px" }}>
                This removes the product from this client so it can be re-provisioned from the
                product list. <strong>It does not delete the database.</strong> Use only before real data exists.
              </div>
              {state.clientProduct.databaseCatalog && (
                <div style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
                  <span>Copy this name and drop it manually in SQL after resetting:</span>
                  <code style={{ fontWeight: "bold" }}>
                    {state.clientProduct.databaseServer
                      ? `${state.clientProduct.databaseServer} / ${state.clientProduct.databaseCatalog}`
                      : state.clientProduct.databaseCatalog}
                  </code>
                  <LButton type="button" fillMode="flat" onClick={() => copyToClipboard(state.clientProduct.databaseCatalog)}>
                    Copy name
                  </LButton>
                </div>
              )}
              {resetState.error && (
                <div style={{ marginTop: "8px", color: "#b00020" }}>{resetState.error}</div>
              )}
              <div style={{ marginTop: "12px" }}>
                <LButton type="button" onClick={cancelReset} disabled={resetState.submitting}>
                  Cancel
                </LButton>
                <span> </span>
                <LButton type="button" themeColor="error" onClick={handleReset} disabled={resetState.submitting}>
                  {resetState.submitting ? "Resetting…" : "Reset provisioning"}
                </LButton>
              </div>
            </div>
          ) : (
            <LButton type="button" onClick={() => setResetState((s) => ({ ...s, confirming: true, error: null }))}>
              Reset provisioning…
            </LButton>
          )}
        </div>
      )}
    </div>
  );
}

export { CPConfigurationManagement };
